import React, { useEffect, useState, useCallback, useRef } from 'react';
import { X, Download, ChevronLeft, ChevronRight, Loader2, AlertCircle, ZoomIn, ZoomOut } from 'lucide-react';

/**
 * Global in-app PDF Viewer Modal (PWA-safe).
 *
 * Renders PDF pages as <canvas> using pdf.js — DOES NOT use iframe (broken on
 * mobile Android Chrome where iframe triggers download prompt) and DOES NOT
 * open new tab (broken in installed PWA).
 *
 * Listens to window event `pdf:open` { detail: { url, title } }.
 *
 * Closes on:
 *   - X button
 *   - Escape key
 *   - Device back button (intercepted via history.pushState + popstate)
 */
export default function PdfViewerModal() {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('PDF');
  const [pdfDoc, setPdfDoc] = useState(null);
  const [pageNum, setPageNum] = useState(1);
  const [numPages, setNumPages] = useState(0);
  const [scale, setScale] = useState(1.0);
  const [status, setStatus] = useState('idle'); // idle | loading | ready | error
  const [errorMsg, setErrorMsg] = useState('');
  const canvasRef = useRef(null);
  const renderTaskRef = useRef(null);
  const pdfjsRef = useRef(null);
  const pdfDocRef = useRef(null);

  // Keep ref in sync with state so `close` can stay stable
  useEffect(() => { pdfDocRef.current = pdfDoc; }, [pdfDoc]);

  const close = useCallback(() => {
    if (renderTaskRef.current) {
      try { renderTaskRef.current.cancel(); } catch (_) { /* noop */ }
      renderTaskRef.current = null;
    }
    if (pdfDocRef.current) {
      try { pdfDocRef.current.destroy(); } catch (_) { /* noop */ }
      pdfDocRef.current = null;
    }
    setOpen(false);
    setUrl('');
    setTitle('PDF');
    setPdfDoc(null);
    setPageNum(1);
    setNumPages(0);
    setScale(1.0);
    setStatus('idle');
    setErrorMsg('');
  }, []);

  // Dynamically load pdf.js once
  const ensurePdfJs = useCallback(async () => {
    if (pdfjsRef.current) return pdfjsRef.current;
    const pdfjs = await import('pdfjs-dist');
    // Worker file copied to /public/pdf.worker.min.mjs (served at root)
    pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';
    pdfjsRef.current = pdfjs;
    return pdfjs;
  }, []);

  // Listen to global open event
  useEffect(() => {
    const handler = (e) => {
      const { url: u, title: t } = e.detail || {};
      if (!u) return;
      setUrl(u);
      setTitle(t || 'PDF');
      setPageNum(1);
      setScale(1.0);
      setStatus('loading');
      setErrorMsg('');
      setOpen(true);
    };
    window.addEventListener('pdf:open', handler);
    return () => window.removeEventListener('pdf:open', handler);
  }, []);

  // Load PDF document when opened
  useEffect(() => {
    if (!open || !url) return;
    let cancelled = false;
    (async () => {
      try {
        const pdfjs = await ensurePdfJs();
        const loadingTask = pdfjs.getDocument({ url, withCredentials: false });
        const doc = await loadingTask.promise;
        if (cancelled) {
          try { doc.destroy(); } catch (_) { /* noop */ }
          return;
        }
        setPdfDoc(doc);
        setNumPages(doc.numPages);
        setStatus('ready');
      } catch (err) {
        if (!cancelled) {
          console.error('[PdfViewerModal] load failed:', err);
          setErrorMsg(err?.message || 'Gagal memuat PDF');
          setStatus('error');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [open, url, ensurePdfJs]);

  // Render current page when page/scale/doc changes
  useEffect(() => {
    if (!pdfDoc || !canvasRef.current || status !== 'ready') return;
    let cancelled = false;
    (async () => {
      try {
        if (renderTaskRef.current) {
          try { renderTaskRef.current.cancel(); } catch (_) { /* noop */ }
          renderTaskRef.current = null;
        }
        const page = await pdfDoc.getPage(pageNum);
        if (cancelled) return;
        const canvas = canvasRef.current;
        if (!canvas) return;
        // Fit width to container, respecting devicePixelRatio for crispness
        const containerWidth = canvas.parentElement?.clientWidth || 360;
        const baseViewport = page.getViewport({ scale: 1 });
        const fitScale = (containerWidth - 16) / baseViewport.width;
        const finalScale = fitScale * scale;
        const dpr = Math.max(1, Math.min(2, window.devicePixelRatio || 1));
        const viewport = page.getViewport({ scale: finalScale * dpr });
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        canvas.style.width = `${viewport.width / dpr}px`;
        canvas.style.height = `${viewport.height / dpr}px`;
        const ctx = canvas.getContext('2d');
        const task = page.render({ canvasContext: ctx, viewport });
        renderTaskRef.current = task;
        await task.promise;
        renderTaskRef.current = null;
      } catch (err) {
        if (err?.name === 'RenderingCancelledException') return;
        console.error('[PdfViewerModal] render failed:', err);
        if (!cancelled) {
          setErrorMsg(err?.message || 'Gagal render halaman');
          setStatus('error');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [pdfDoc, pageNum, scale, status]);

  // Escape + device back
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    const stateMarker = { __pdfModal: true, ts: Date.now() };
    window.history.pushState(stateMarker, '');
    const onPop = () => { close(); };
    window.addEventListener('keydown', onKey);
    window.addEventListener('popstate', onPop);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('popstate', onPop);
      document.body.style.overflow = prevOverflow;
      if (window.history.state && window.history.state.__pdfModal) {
        try { window.history.back(); } catch (_e) { /* noop */ }
      }
    };
  }, [open, close]);

  const handleDownload = () => {
    try {
      const a = document.createElement('a');
      a.href = url;
      a.download = `${title || 'document'}.pdf`;
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      setTimeout(() => a.parentNode && a.parentNode.removeChild(a), 100);
    } catch (_e) { /* noop */ }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/90 flex flex-col"
      data-testid="pdf-viewer-modal"
    >
      {/* Header — sticky toolbar */}
      <div className="flex items-center justify-between gap-2 px-2 sm:px-3 py-2 bg-gray-900 text-white shrink-0">
        <span className="text-xs sm:text-sm font-semibold truncate flex-1 min-w-0" data-testid="pdf-modal-title">{title}</span>
        <div className="flex items-center gap-1 shrink-0">
          {numPages > 1 && (
            <div className="flex items-center gap-1 mr-1 bg-gray-800 rounded px-1">
              <button
                onClick={() => setPageNum((p) => Math.max(1, p - 1))}
                disabled={pageNum <= 1}
                className="p-1.5 rounded disabled:opacity-30 active:bg-gray-700"
                aria-label="Previous page"
                data-testid="pdf-modal-prev-btn"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-[11px] font-mono px-1 select-none" data-testid="pdf-modal-page-indicator">{pageNum}/{numPages}</span>
              <button
                onClick={() => setPageNum((p) => Math.min(numPages, p + 1))}
                disabled={pageNum >= numPages}
                className="p-1.5 rounded disabled:opacity-30 active:bg-gray-700"
                aria-label="Next page"
                data-testid="pdf-modal-next-btn"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
          <button
            onClick={() => setScale((s) => Math.max(0.5, s - 0.25))}
            className="p-2 rounded hover:bg-gray-800 active:bg-gray-700 hidden sm:inline-flex"
            aria-label="Zoom out"
            data-testid="pdf-modal-zoomout-btn"
            disabled={status !== 'ready'}
          >
            <ZoomOut className="w-4 h-4" />
          </button>
          <button
            onClick={() => setScale((s) => Math.min(3, s + 0.25))}
            className="p-2 rounded hover:bg-gray-800 active:bg-gray-700 hidden sm:inline-flex"
            aria-label="Zoom in"
            data-testid="pdf-modal-zoomin-btn"
            disabled={status !== 'ready'}
          >
            <ZoomIn className="w-4 h-4" />
          </button>
          <button
            onClick={handleDownload}
            className="p-2 rounded hover:bg-gray-800 active:bg-gray-700"
            aria-label="Download PDF"
            data-testid="pdf-modal-download-btn"
            title="Download"
          >
            <Download className="w-5 h-5" />
          </button>
          <button
            onClick={close}
            className="p-2 rounded hover:bg-gray-800 active:bg-gray-700"
            aria-label="Close"
            data-testid="pdf-modal-close-btn"
            title="Tutup"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="relative flex-1 overflow-auto bg-gray-700 flex items-start justify-center p-2 sm:p-4">
        {status === 'loading' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-white z-10 pointer-events-none">
            <Loader2 className="w-8 h-8 animate-spin" />
            <span className="text-xs mt-2">Memuat PDF...</span>
          </div>
        )}
        {status === 'error' && (
          <div className="bg-white rounded-lg p-6 max-w-sm mx-auto my-auto text-center" data-testid="pdf-modal-error">
            <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-2" />
            <p className="text-sm font-semibold text-gray-900 mb-1">Gagal memuat PDF</p>
            <p className="text-xs text-gray-600 mb-4 break-words">{errorMsg}</p>
            <button
              onClick={handleDownload}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
              data-testid="pdf-modal-fallback-download-btn"
            >
              <Download className="w-4 h-4 inline mr-1" /> Download PDF
            </button>
          </div>
        )}
        <canvas
          ref={canvasRef}
          className={status === 'ready' ? 'shadow-lg bg-white max-w-full' : 'hidden'}
          data-testid="pdf-modal-canvas"
        />
      </div>
    </div>
  );
}
